import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, Profile } from '../context/AuthContext';
import { adminService, AssignedPlace, DuplicateGroup } from '../../services/adminService';
import {
  CohortPatientOverview,
  ControlPlace,
  GlucoseTargets,
  GlucoseThresholds,
  PatientProgress,
  SocioEconomicData
} from '../../types/domain';
import GlucoseTargetEditor from '../components/GlucoseTargetEditor';
import CohortMonitor from '../components/CohortMonitor';
import PatientProgressPanel from '../components/PatientProgressPanel';
import PatientSocioEconomicPanel from '../components/PatientSocioEconomicPanel';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { getGlucoseZone, getMeasurementContextLabel } from '../../lib/domain-data';

// Four zones, matching .glucose-status in global.css (FR-07). The doctor table
// previously used a two-band `> 140` rule that agreed with no other screen.
const glucoseZoneBadgeClasses: Record<string, string> = {
  'low': 'bg-sky-50 border-sky-200 text-sky-800',
  'normal': 'bg-emerald-50 border-emerald-200 text-emerald-800',
  'warning': 'bg-yellow-50 border-yellow-200 text-yellow-800',
  'high': 'bg-red-50 border-red-200 text-red-800'
};

/** The monitoring window the dashboard opens on (FR-14). */
const DEFAULT_WINDOW_DAYS = 14;

const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const isSuperadmin = profile?.role === 'superadmin';

  const [patients, setPatients] = useState<Profile[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [activeTab, setActiveTab] = useState('monitoring');
  const [patientQuery, setPatientQuery] = useState('');

  // Selected patient for viewing history
  const [selectedPatient, setSelectedPatient] = useState<Profile | null>(null);
  const [patientHistory, setPatientHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // FR-14 cohort monitoring
  const [cohort, setCohort] = useState<CohortPatientOverview[]>([]);
  const [loadingCohort, setLoadingCohort] = useState(true);
  const [cohortError, setCohortError] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
  const [progress, setProgress] = useState<PatientProgress | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [progressError, setProgressError] = useState<string | null>(null);

  // Superadmin: Doctor management
  const [doctors, setDoctors] = useState<(Profile & { assigned_places: AssignedPlace[] })[]>([]);
  const [controlPlaces, setControlPlaces] = useState<ControlPlace[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [newPlaceName, setNewPlaceName] = useState('');
  const [placeBusy, setPlaceBusy] = useState(false);
  const [placeError, setPlaceError] = useState<string | null>(null);
  const [mergeSource, setMergeSource] = useState<string>('');
  const [mergeTarget, setMergeTarget] = useState<string>('');

  // FR-06 target ranges
  const [globalTargets, setGlobalTargets] = useState<GlucoseTargets | null>(null);
  const [patientTargets, setPatientTargets] = useState<GlucoseTargets | null>(null);
  const [targetsBusy, setTargetsBusy] = useState(false);
  const [targetsError, setTargetsError] = useState<string | null>(null);
  const [targetsNotice, setTargetsNotice] = useState<string | null>(null);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<string | null>(null);
  const [selectedControlPlace, setSelectedControlPlace] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Add new doctor
  const [addDoctorOpen, setAddDoctorOpen] = useState(false);
  const [newDoctorName, setNewDoctorName] = useState('');
  const [newDoctorEmail, setNewDoctorEmail] = useState('');
  const [newDoctorPassword, setNewDoctorPassword] = useState('');
  const [addDoctorError, setAddDoctorError] = useState<string | null>(null);
  const [isCreatingDoctor, setIsCreatingDoctor] = useState(false);

  useEffect(() => {
    if (profile) {
      loadPatients();
      loadGlobalTargets();
      if (isSuperadmin) {
        loadDoctorsAndPlaces();
      }
    }
  }, [profile]);

  // Separate from loadPatients: the roster reloads when the doctor changes the
  // window, and re-fetching the profile list along with it would be wasted.
  useEffect(() => {
    if (!profile) {
      return;
    }
    let cancelled = false;
    setLoadingCohort(true);
    setCohortError(null);
    adminService
      // A superadmin sees every patient; a doctor sees their assigned places,
      // which the function resolves server-side.
      .getCohortOverview(isSuperadmin ? null : profile.id, windowDays)
      .then(rows => {
        if (!cancelled) {
          setCohort(rows);
        }
      })
      .catch(error => {
        console.error('Error loading cohort overview:', error);
        if (!cancelled) {
          setCohort([]);
          setCohortError(
            'Gagal memuat ringkasan pemantauan. Pastikan migrasi 011 sudah dijalankan.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCohort(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [profile, isSuperadmin, windowDays]);

  // The selected patient's charts follow the same window as the roster, so the
  // "3× hipoglikemia" a doctor clicked on is the one the chart then draws.
  useEffect(() => {
    if (!selectedPatient) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    setLoadingProgress(true);
    setProgressError(null);
    adminService
      .getPatientProgress(selectedPatient.id, windowDays)
      .then(found => {
        if (!cancelled) {
          setProgress(found);
        }
      })
      .catch(error => {
        console.error('Error loading patient progress:', error);
        if (!cancelled) {
          setProgress(null);
          setProgressError('Gagal memuat grafik perkembangan pasien.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProgress(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPatient, windowDays]);

  const loadGlobalTargets = async () => {
    try {
      setGlobalTargets(await adminService.getGlobalTargets());
    } catch (error) {
      console.error('Error loading glucose targets:', error);
    }
  };

  const loadPatients = async () => {
    setLoadingPatients(true);
    try {
      if (isSuperadmin) {
        const data = await adminService.getAllPatients();
        setPatients(data);
      } else {
        const data = await adminService.getPatientsForDoctor(profile!.id);
        setPatients(data);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadDoctorsAndPlaces = async () => {
    setLoadingDoctors(true);
    try {
      const [docs, places, dupes] = await Promise.all([
        adminService.getDoctors(),
        adminService.getControlPlaces(),
        adminService.getPossibleDuplicates().catch(() => [] as DuplicateGroup[])
      ]);
      setDoctors(docs);
      setControlPlaces(places);
      setDuplicates(dupes);
    } catch (error) {
      console.error('Error loading doctors & places:', error);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const runTargetAction = async (action: () => Promise<unknown>, notice: string) => {
    if (!profile) return;
    setTargetsBusy(true);
    setTargetsError(null);
    setTargetsNotice(null);
    try {
      await action();
      setTargetsNotice(notice);
    } catch (error: any) {
      setTargetsError(error?.message || 'Gagal menyimpan target gula darah.');
    } finally {
      setTargetsBusy(false);
    }
  };

  const handleSaveGlobalTargets = (pre: GlucoseThresholds, post: GlucoseThresholds) =>
    runTargetAction(async () => {
      await adminService.updateGlobalTargets(pre, post, profile!.id);
      await loadGlobalTargets();
    }, 'Target umum tersimpan.');

  const handleSavePatientTargets = (pre: GlucoseThresholds, post: GlucoseThresholds) =>
    runTargetAction(async () => {
      await adminService.upsertPatientTargets(selectedPatient!.id, pre, post, profile!.id);
      setPatientTargets(await adminService.getPatientTargets(selectedPatient!.id));
    }, 'Target khusus pasien tersimpan.');

  const handleClearPatientTargets = () =>
    runTargetAction(async () => {
      await adminService.clearPatientTargets(selectedPatient!.id);
      setPatientTargets(null);
    }, 'Pasien kembali mengikuti target umum.');

  /**
   * Folds an FR-03 save back into the rows already on screen.
   *
   * Cheaper and less jarring than re-running loadPatients(), which would
   * rebuild the list and lose the scroll position for a four-field edit.
   */
  const handleSocioEconomicSaved = (patientId: string, data: SocioEconomicData) => {
    const apply = (row: Profile): Profile =>
      row.id === patientId
        ? {
            ...row,
            parent_education: data.parentEducation,
            family_income: data.familyIncome,
            financing_type: data.financingType,
            parent_contact: data.parentContact
          }
        : row;

    setPatients(prev => prev.map(apply));
    setSelectedPatient(prev => (prev ? apply(prev) : prev));
  };

  const pendingPlaces = controlPlaces.filter(place => place.status === 'pending');

  // A clinic list is long enough to scroll past the child you came for
  // (NFR-02). The list itself is one query; this narrows what is rendered.
  const patientNeedle = patientQuery.trim().toLowerCase();
  const visiblePatients = patientNeedle
    ? patients.filter(
        p =>
          (p.full_name ?? '').toLowerCase().includes(patientNeedle) ||
          (p.control_place ?? '').toLowerCase().includes(patientNeedle)
      )
    : patients;

  const handleSelectPatient = async (patient: Profile) => {
    setSelectedPatient(patient);
    setLoadingHistory(true);
    setTargetsError(null);
    setTargetsNotice(null);
    setPatientTargets(null);
    try {
      const [history, targets] = await Promise.all([
        adminService.getPatientHistory(patient.id),
        adminService.getPatientTargets(patient.id).catch(() => null)
      ]);
      setPatientHistory(history);
      setPatientTargets(targets);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  /**
   * Opens a patient from the monitoring roster.
   *
   * The roster carries its own rows rather than Profile objects, so this looks
   * the patient up in the already-loaded list instead of re-fetching — the two
   * queries run off the same cohort. They are two round trips though, and the
   * roster can win the race, so a miss falls back to fetching the one profile
   * rather than doing nothing when the doctor clicks.
   */
  const handleSelectPatientById = async (patientId: string) => {
    setActiveTab('patients');
    const known = patients.find(p => p.id === patientId);
    if (known) {
      handleSelectPatient(known);
      return;
    }
    try {
      const found = await adminService.getPatientProfile(patientId);
      if (found) {
        handleSelectPatient(found);
      }
    } catch (error) {
      console.error('Error loading patient profile:', error);
    }
  };

  const handleAssignDoctor = async () => {
    if (!selectedDoctor || !selectedControlPlace) return;
    setIsAssigning(true);
    try {
      await adminService.assignDoctorToControlPlace(selectedDoctor, selectedControlPlace);
      await loadDoctorsAndPlaces();
      setSelectedControlPlace('');
    } catch (error) {
      console.error('Error assigning doctor:', error);
      alert('Gagal assign dokter. Mungkin sudah di-assign ke tempat ini.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveDoctor = async (doctorId: string, controlPlaceId: string | null) => {
    if (!controlPlaceId) return;
    if (!window.confirm('Hapus tempat kontrol ini dari dokter?')) return;
    try {
      await adminService.removeDoctorFromControlPlace(doctorId, controlPlaceId);
      await loadDoctorsAndPlaces();
    } catch (error) {
      console.error('Error removing doctor control place:', error);
    }
  };

  const runPlaceAction = async (action: () => Promise<unknown>) => {
    setPlaceBusy(true);
    setPlaceError(null);
    try {
      await action();
      await loadDoctorsAndPlaces();
      await loadPatients();
    } catch (error: any) {
      setPlaceError(error?.message || 'Aksi tempat kontrol gagal.');
    } finally {
      setPlaceBusy(false);
    }
  };

  const handleCreatePlace = () =>
    runPlaceAction(async () => {
      await adminService.createControlPlace(newPlaceName);
      setNewPlaceName('');
    });

  const handleApprovePlace = (placeId: string) =>
    runPlaceAction(() => adminService.approveControlPlace(placeId));

  const handleRenamePlace = (placeId: string, current: string) => {
    const next = window.prompt('Nama baru tempat kontrol:', current);
    if (!next || next.trim() === current) return;
    return runPlaceAction(() => adminService.renameControlPlace(placeId, next));
  };

  const handleMergePlaces = () => {
    if (!mergeSource || !mergeTarget || mergeSource === mergeTarget) return;
    const from = controlPlaces.find(p => p.id === mergeSource)?.name ?? '';
    const to = controlPlaces.find(p => p.id === mergeTarget)?.name ?? '';
    if (!window.confirm(
      `Gabungkan "${from}" ke dalam "${to}"?\n\n` +
      `Semua pasien dan penugasan dokter akan dipindahkan, lalu "${from}" dihapus.`
    )) return;
    return runPlaceAction(async () => {
      await adminService.mergeControlPlaces(mergeSource, mergeTarget);
      setMergeSource('');
      setMergeTarget('');
    });
  };

  const handleAddDoctor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDoctorName.trim() || !newDoctorEmail.trim() || !newDoctorPassword.trim()) return;
    setIsCreatingDoctor(true);
    setAddDoctorError(null);
    try {
      await adminService.createDoctorUser(newDoctorEmail.trim(), newDoctorPassword, newDoctorName.trim());
      await loadDoctorsAndPlaces();
      setAddDoctorOpen(false);
      setNewDoctorName('');
      setNewDoctorEmail('');
      setNewDoctorPassword('');
    } catch (err: any) {
      setAddDoctorError(err.message || 'Gagal membuat akun dokter.');
    } finally {
      setIsCreatingDoctor(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8 space-y-6">
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Selamat datang, {profile?.full_name} ({profile?.role})
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="monitoring">Pemantauan</TabsTrigger>
          <TabsTrigger value="patients">Pasien</TabsTrigger>
          {isSuperadmin && <TabsTrigger value="doctors">Manajemen Dokter</TabsTrigger>}
          {isSuperadmin && <TabsTrigger value="places">Tempat Kontrol</TabsTrigger>}
          {isSuperadmin && <TabsTrigger value="targets">Target Gula Darah</TabsTrigger>}
        </TabsList>

        {/* FR-14. First tab on purpose: the dashboard's job is to surface the
            child who needs attention before the doctor goes looking. */}
        <TabsContent value="monitoring" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pemantauan Pasien</CardTitle>
              <CardDescription>
                Ringkasan gula darah, insulin, HbA1c dan frekuensi input, dengan alarm
                hipoglikemia berdasarkan target masing-masing pasien.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {cohortError ? (
                <div className="target-band-error" role="alert">
                  {cohortError}
                </div>
              ) : (
                <CohortMonitor
                  rows={cohort}
                  loading={loadingCohort}
                  windowDays={windowDays}
                  onWindowChange={setWindowDays}
                  onSelectPatient={handleSelectPatientById}
                  selectedPatientId={selectedPatient?.id ?? null}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patients" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Patients List */}
            <Card className="col-span-1 md:col-span-1">
              <CardHeader>
                <CardTitle>Daftar Pasien</CardTitle>
                <CardDescription>
                  {isSuperadmin ? 'Semua pasien yang terdaftar' : 'Pasien di bawah pengawasan Anda'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="search"
                  className="cohort-search mb-3"
                  placeholder="Cari nama atau tempat kontrol"
                  aria-label="Cari pasien"
                  value={patientQuery}
                  onChange={(e) => setPatientQuery(e.target.value)}
                />
                {loadingPatients ? (
                  <div className="text-sm">Memuat pasien...</div>
                ) : visiblePatients.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    {patients.length === 0
                      ? 'Tidak ada pasien.'
                      : 'Tidak ada pasien yang cocok dengan pencarian.'}
                  </div>
                ) : (
                  <ScrollArea className="h-[500px] w-full pr-4">
                    <div className="space-y-2">
                      {visiblePatients.map((p) => (
                        <Button
                          key={p.id}
                          variant={selectedPatient?.id === p.id ? "default" : "outline"}
                          className="w-full justify-start text-left h-auto py-3"
                          onClick={() => handleSelectPatient(p)}
                        >
                          <div className="flex flex-col items-start gap-1 w-full overflow-hidden">
                            <span className="font-semibold truncate w-full">{p.full_name}</span>
                            <div className="flex justify-between w-full text-xs opacity-80">
                              <span>{p.diabetes_type}</span>
                              <span className="truncate max-w-[50%]">{p.control_place}</span>
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Patient Detail & History */}
            <Card className="col-span-1 md:col-span-2">
              <CardHeader>
                <CardTitle>
                  {selectedPatient ? `Riwayat: ${selectedPatient.full_name}` : 'Pilih pasien untuk melihat riwayat'}
                </CardTitle>
                {selectedPatient && (
                  <CardDescription>
                    {selectedPatient.diabetes_type} | Tempat Kontrol: {selectedPatient.control_place}
                  </CardDescription>
                )}
                {selectedPatient && (
                  <Link className="dashboard-report-link" to={`/monthly-report?patient=${selectedPatient.id}`}>
                    Lihat laporan bulanan
                  </Link>
                )}
              </CardHeader>
              <CardContent>
                {selectedPatient ? (
                  <details className="patient-targets">
                    <summary>
                      Target gula darah
                      <span className="patient-targets-state">
                        {patientTargets ? 'khusus pasien ini' : 'mengikuti target umum'}
                      </span>
                    </summary>
                    <GlucoseTargetEditor
                      targets={patientTargets}
                      inheritedFrom={globalTargets}
                      busy={targetsBusy}
                      error={targetsError}
                      onSave={handleSavePatientTargets}
                      onClear={handleClearPatientTargets}
                      clearLabel="Ikuti target umum"
                    />
                    {targetsNotice ? (
                      <div className="target-band-preview">{targetsNotice}</div>
                    ) : null}
                  </details>
                ) : null}

                {selectedPatient ? (
                  <PatientSocioEconomicPanel
                    key={selectedPatient.id}
                    patient={selectedPatient}
                    canEdit={isSuperadmin}
                    onSaved={data => handleSocioEconomicSaved(selectedPatient.id, data)}
                  />
                ) : null}

                {selectedPatient ? (
                  <PatientProgressPanel
                    progress={progress}
                    loading={loadingProgress}
                    error={progressError}
                  />
                ) : null}

                {!selectedPatient ? (
                  <div className="flex justify-center items-center h-48 text-muted-foreground">
                    Belum ada pasien yang dipilih.
                  </div>
                ) : loadingHistory ? (
                  <div className="text-sm">Memuat riwayat...</div>
                ) : patientHistory.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Belum ada catatan riwayat untuk pasien ini.</div>
                ) : (
                  <ScrollArea className="h-[450px] rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tanggal</TableHead>
                          <TableHead>Waktu</TableHead>
                          <TableHead>Gula Darah</TableHead>
                          <TableHead>Waktu Pemeriksaan</TableHead>
                          <TableHead>Dosis Insulin</TableHead>
                          <TableHead>Jenis/Brand</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {patientHistory.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{h.date}</TableCell>
                            <TableCell>{h.time?.slice(0, 5)}</TableCell>
                            <TableCell>
                              {h.glucose_value ? (
                                <Badge
                                  variant="outline"
                                  className={glucoseZoneBadgeClasses[
                                    getGlucoseZone(h.glucose_value) ?? 'normal'
                                  ]}
                                >
                                  {h.glucose_value} mg/dL
                                </Badge>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {h.glucose_value
                                ? getMeasurementContextLabel(h.measurement_context)
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {h.insulin_dose ? `${h.insulin_dose} unit` : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col text-xs">
                                <span>{h.insulin_type_id?.replace('type-', '')}</span>
                                <span className="text-muted-foreground">{h.insulin_brand}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {isSuperadmin && (
          <TabsContent value="doctors" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Daftar Dokter</CardTitle>
                    <CardDescription className="mt-1">
                      Kelola penugasan tempat kontrol untuk setiap dokter. Pilihan diambil dari
                      daftar di tab Tempat Kontrol.
                    </CardDescription>
                  </div>

                  {/* Add New Doctor Dialog */}
                  <Dialog open={addDoctorOpen} onOpenChange={(open) => {
                    setAddDoctorOpen(open);
                    if (!open) { setAddDoctorError(null); }
                  }}>
                    <DialogTrigger asChild>
                      <Button>+ Tambah Dokter</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Tambah Dokter Baru</DialogTitle>
                        <DialogDescription>
                          Buat akun pengguna baru dengan role dokter. Dokter dapat login menggunakan email dan password yang diisi.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddDoctor} className="space-y-4 py-2">
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor="doctor-name">Nama Lengkap</label>
                          <input
                            id="doctor-name"
                            type="text"
                            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="dr. John Doe"
                            value={newDoctorName}
                            onChange={(e) => setNewDoctorName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor="doctor-email">Email</label>
                          <input
                            id="doctor-email"
                            type="email"
                            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="dokter@email.com"
                            value={newDoctorEmail}
                            onChange={(e) => setNewDoctorEmail(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor="doctor-password">Password</label>
                          <input
                            id="doctor-password"
                            type="password"
                            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="Minimal 6 karakter"
                            minLength={6}
                            value={newDoctorPassword}
                            onChange={(e) => setNewDoctorPassword(e.target.value)}
                            required
                          />
                        </div>

                        {addDoctorError && (
                          <p className="text-sm text-destructive">{addDoctorError}</p>
                        )}

                        <Separator />
                        <Button type="submit" className="w-full" disabled={isCreatingDoctor}>
                          {isCreatingDoctor ? 'Membuat akun...' : 'Buat Akun Dokter'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDoctors ? (
                  <div>Memuat data dokter...</div>
                ) : doctors.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                    <p className="text-sm">Belum ada dokter yang terdaftar.</p>
                    <p className="text-xs">Klik "+ Tambah Dokter" untuk menambahkan dokter baru.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama Dokter</TableHead>
                        <TableHead>Tempat Kontrol (Ditugaskan)</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {doctors.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">{doc.full_name || 'Tanpa Nama'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {doc.assigned_places.length > 0 ? (
                                doc.assigned_places.map(place => (
                                  <Badge key={place.id ?? place.name} variant="secondary" className="flex items-center gap-1">
                                    {place.name}
                                    <button
                                      onClick={() => handleRemoveDoctor(doc.id, place.id)}
                                      className="text-xs hover:text-destructive ml-1"
                                      title="Hapus tempat kontrol"
                                    >
                                      ×
                                    </button>
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">Belum ada penugasan</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => setSelectedDoctor(doc.id)}>
                                  Assign
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Assign Tempat Kontrol</DialogTitle>
                                  <DialogDescription>
                                    Pilih tempat kontrol dari daftar di bawah ini untuk ditugaskan ke {doc.full_name}.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <Select value={selectedControlPlace} onValueChange={setSelectedControlPlace}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Pilih tempat kontrol" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {controlPlaces.map(place => (
                                        <SelectItem key={place.id} value={place.id}>
                                          {place.name}
                                          {place.status === 'pending' ? ' (menunggu persetujuan)' : ''}
                                        </SelectItem>
                                      ))}
                                      {controlPlaces.length === 0 && (
                                        <SelectItem value="none" disabled>Tidak ada tempat kontrol terdaftar</SelectItem>
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <Button 
                                    className="w-full" 
                                    onClick={handleAssignDoctor} 
                                    disabled={!selectedControlPlace || isAssigning}
                                  >
                                    {isAssigning ? 'Menyimpan...' : 'Simpan Penugasan'}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="places" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Tempat Kontrol</CardTitle>
                <CardDescription>
                  Daftar rumah sakit / klinik yang bisa dipilih pasien. Dokter ditugaskan
                  ke tempat dari daftar ini, dan pasien dicocokkan berdasarkan id — bukan
                  tulisan bebas.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {placeError && (
                  <div className="text-sm text-destructive">{placeError}</div>
                )}

                <div className="flex gap-2">
                  <input
                    className="flex-1 rounded-md border px-3 py-2 text-sm"
                    value={newPlaceName}
                    onChange={(e) => setNewPlaceName(e.target.value)}
                    placeholder="Tambah tempat kontrol baru"
                    aria-label="Nama tempat kontrol baru"
                  />
                  <Button onClick={handleCreatePlace} disabled={placeBusy || !newPlaceName.trim()}>
                    Tambah
                  </Button>
                </div>

                {pendingPlaces.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">
                      Menunggu persetujuan ({pendingPlaces.length})
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Ditambahkan pasien lewat &quot;Lainnya&quot;. Setujui jika benar tempat
                      baru, atau gabungkan ke tempat yang sudah ada di bawah.
                    </p>
                    {pendingPlaces.map(place => (
                      <div key={place.id} className="flex items-center justify-between rounded-md border p-2">
                        <span className="text-sm">{place.name}</span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" disabled={placeBusy}
                            onClick={() => handleRenamePlace(place.id, place.name)}>
                            Ubah nama
                          </Button>
                          <Button size="sm" disabled={placeBusy}
                            onClick={() => handleApprovePlace(place.id)}>
                            Setujui
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {duplicates.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">
                      Kemungkinan duplikat ({duplicates.length})
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Migrasi hanya menggabungkan nama yang identik setelah perbedaan huruf
                      besar/kecil dan spasi. Nama di bawah ini mirip tapi tidak digabung
                      otomatis — itu keputusan admin.
                    </p>
                    {duplicates.map(group => (
                      <div key={group.core} className="rounded-md border p-2 text-sm">
                        {group.names.join('  •  ')}
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Gabungkan tempat kontrol</h3>
                  <p className="text-xs text-muted-foreground">
                    Semua pasien dan penugasan dokter dipindahkan, lalu tempat asal dihapus.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Select value={mergeSource} onValueChange={setMergeSource}>
                      <SelectTrigger className="sm:w-[240px]">
                        <SelectValue placeholder="Gabungkan dari..." />
                      </SelectTrigger>
                      <SelectContent>
                        {controlPlaces.map(place => (
                          <SelectItem key={place.id} value={place.id}>{place.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">ke dalam</span>
                    <Select value={mergeTarget} onValueChange={setMergeTarget}>
                      <SelectTrigger className="sm:w-[240px]">
                        <SelectValue placeholder="Gabungkan ke..." />
                      </SelectTrigger>
                      <SelectContent>
                        {controlPlaces
                          .filter(place => place.id !== mergeSource)
                          .map(place => (
                            <SelectItem key={place.id} value={place.id}>{place.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button variant="destructive" disabled={placeBusy || !mergeSource || !mergeTarget}
                      onClick={handleMergePlaces}>
                      Gabungkan
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">
                    Semua tempat kontrol ({controlPlaces.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {controlPlaces.map(place => (
                        <TableRow key={place.id}>
                          <TableCell className="font-medium">{place.name}</TableCell>
                          <TableCell>
                            <Badge variant={place.status === 'pending' ? 'destructive' : 'secondary'}>
                              {place.status === 'pending' ? 'Menunggu' : 'Aktif'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" disabled={placeBusy}
                              onClick={() => handleRenamePlace(place.id, place.name)}>
                              Ubah nama
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {controlPlaces.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            Belum ada tempat kontrol.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="targets" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Target Gula Darah Umum</CardTitle>
                <CardDescription>
                  Dipakai semua pasien yang belum punya target khusus. Dokter bisa
                  menyetel target khusus per pasien dari tab Pasien. Perubahan langsung
                  berlaku pada pewarnaan dan perhitungan laporan.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GlucoseTargetEditor
                  targets={globalTargets}
                  busy={targetsBusy}
                  error={targetsError}
                  onSave={handleSaveGlobalTargets}
                />
                {targetsNotice ? (
                  <div className="target-band-preview">{targetsNotice}</div>
                ) : null}
                {globalTargets?.updatedAt ? (
                  <div className="target-band-hint" style={{ marginTop: 8 }}>
                    Terakhir diubah: {new Date(globalTargets.updatedAt).toLocaleString('id-ID')}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Dashboard;
